import { ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const PageLoader = () => (
  <div className="flex h-96 items-center justify-center" data-testid="page-loader">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

// Returns to the page the user came from; falls back to a parent route when opened directly.
export const BackButton = ({ fallback = "/", testId = "page-back-btn" }) => {
  const navigate = useNavigate();
  const go = () => ((window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate(fallback, { replace: true }));
  return (
    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={go} aria-label="Back" title="Back" data-testid={testId}>
      <ArrowLeft className="h-5 w-5" />
    </Button>
  );
};

export const PageHeader = ({ icon: Icon, title, subtitle, actions, back, iconMuted = false, testId = "page-header" }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid={testId}>
    <div className="min-w-0 flex items-start gap-3">
      {back && <BackButton fallback={back} />}
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="page-title">
          {Icon && <Icon className={cn("h-6 w-6 shrink-0", iconMuted ? "text-muted-foreground" : "text-primary")} />}
          <span className="truncate">{title}</span>
        </h1>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const EmptyState = ({ icon: Icon, title, description, action, testId = "empty-state" }) => (
  <div className="flex flex-col items-center justify-center p-12 text-muted-foreground text-center" data-testid={testId}>
    {Icon && <Icon className="h-12 w-12 text-muted-foreground mb-3" />}
    <p className="text-lg font-medium text-foreground">{title}</p>
    {description && <p className="text-sm mt-1 max-w-md">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const InlineAlert = ({ icon: Icon, children, testId = "inline-alert" }) => (
  <div
    className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm"
    data-testid={testId}
    role="alert"
  >
    {Icon && <Icon className="h-4 w-4 mt-0.5 text-red-500 shrink-0" />}
    <div>{children}</div>
  </div>
);

export const SpinnerButton = ({ loading, icon: Icon, children, ...props }) => (
  <Button disabled={loading || props.disabled} {...props}>
    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : Icon ? <Icon className="h-4 w-4 mr-2" /> : null}
    {children}
  </Button>
);

export const SectionLabel = ({ children }) => (
  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{children}</p>
);
